import { type ReactNode, useState } from 'react';
import { Box, Divider, Flex, Menu, Stack, Switch, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import type { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import type { TemplateWithoutSections } from 'src/data-capture/hooks/templates/useTemplates';
import { Button } from 'src/design-system/Button';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Props = {
  templates: TemplateWithoutSections[];
  defaultTemplateId?: string;
  agendaFiles: UploadedFile[];
  defaultAgendaFileId?: string;
  isGenerating: boolean;
  showSummariseToggle: boolean;
  onGenerateTemplate: (templateId: string, summarise: boolean) => void;
  onGenerateAgenda: (agendaFileId: string, summarise: boolean) => void;
};

const switchStyles = { track: { width: 28, minWidth: 28, height: 16 } };

export const StructureSelection = ({
  templates,
  defaultTemplateId,
  agendaFiles,
  defaultAgendaFileId,
  isGenerating,
  showSummariseToggle,
  onGenerateTemplate,
  onGenerateAgenda,
}: Props) => {
  const [templateId, setTemplateId] = useState(defaultTemplateId ?? templates[0]?.id);
  const [templateSummarise, setTemplateSummarise] = useState(true);
  const [agendaFileId, setAgendaFileId] = useState(defaultAgendaFileId ?? agendaFiles[0]?.id);
  const [agendaSummarise, setAgendaSummarise] = useState(true);
  const [generatingOption, setGeneratingOption] = useState<'template' | 'agenda'>();

  const selectedTemplate = templates.find(template => template.id === templateId);
  const selectedAgenda = agendaFiles.find(file => file.id === agendaFileId);

  return (
    <Stack gap={0}>
      <Text size="lg" fw={FONT_WEIGHTS.semibold} c="black">
        How would you like to structure the meeting note?
      </Text>

      <Stack gap="xs" mt="sm">
        <OptionBox
          title="Use a template"
          subtitle="Generate meeting note using a predefined template"
          isGenerating={isGenerating && generatingOption === 'template'}
          generateDisabled={!templateId || isGenerating}
          onGenerate={() => {
            if (!templateId) return;
            setGeneratingOption('template');
            onGenerateTemplate(templateId, templateSummarise);
          }}
          showSummariseToggle={showSummariseToggle}
          summarise={templateSummarise}
          onSummariseChange={setTemplateSummarise}
          dropdownLabel="Template"
          dropdownValue={selectedTemplate?.displayName}
          options={templates.map(template => ({ id: template.id, label: template.displayName }))}
          onSelectOption={setTemplateId}
        />

        <OptionBox
          title="Use an agenda"
          subtitle="Generate meeting note using an agenda file as the structure"
          isGenerating={isGenerating && generatingOption === 'agenda'}
          generateDisabled={!agendaFileId || isGenerating}
          onGenerate={() => {
            if (!agendaFileId) return;
            setGeneratingOption('agenda');
            onGenerateAgenda(agendaFileId, agendaSummarise);
          }}
          showSummariseToggle={showSummariseToggle}
          summarise={agendaSummarise}
          onSummariseChange={setAgendaSummarise}
          dropdownLabel="Agenda"
          dropdownValue={selectedAgenda?.file.fileName}
          options={agendaFiles.map(file => ({ id: file.id, label: file.file.fileName }))}
          onSelectOption={setAgendaFileId}
        />
      </Stack>
    </Stack>
  );
};

type OptionBoxProps = {
  title: string;
  subtitle: string;
  isGenerating: boolean;
  generateDisabled: boolean;
  onGenerate: () => void;
  showSummariseToggle: boolean;
  summarise: boolean;
  onSummariseChange: (value: boolean) => void;
  dropdownLabel: string;
  dropdownValue?: string;
  options: { id: string; label: string }[];
  onSelectOption: (id: string) => void;
};

function OptionBox({
  title,
  subtitle,
  isGenerating,
  generateDisabled,
  onGenerate,
  showSummariseToggle,
  summarise,
  onSummariseChange,
  dropdownLabel,
  dropdownValue,
  options,
  onSelectOption,
}: OptionBoxProps) {
  return (
    <Box bg="gray.0" p="sm" style={{ borderRadius: 12 }}>
      <Flex justify="space-between" align="center" gap="sm">
        <Box style={{ minWidth: 0 }}>
          <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
            {title}
          </Text>
          <Text size="xs" fw={FONT_WEIGHTS.regular} c="gray.7" mt={2}>
            {subtitle}
          </Text>
        </Box>
        <Button
          variant="primary"
          size="sm"
          onClick={onGenerate}
          loading={isGenerating}
          disabled={generateDisabled}
          style={{ flexShrink: 0 }}
        >
          Generate
        </Button>
      </Flex>

      <Divider color="gray.1" mt="xxs" />

      <Flex gap="xxs" mt="xxs">
        {showSummariseToggle && (
          <SelectorBox>
            <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
              Summarise meeting note
            </Text>
            <Switch
              checked={summarise}
              onChange={event => onSummariseChange(event.currentTarget.checked)}
              color="brand.6"
              styles={switchStyles}
            />
          </SelectorBox>
        )}

        <SelectorBox>
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
            {dropdownLabel}
          </Text>
          <Menu position="bottom-end" width={220} withinPortal>
            <Menu.Target>
              <Flex align="center" gap="xxxs" style={{ cursor: 'pointer', minWidth: 0 }}>
                <Text
                  size="sm"
                  fw={FONT_WEIGHTS.medium}
                  c="gray.7"
                  lineClamp={1}
                  style={{ maxWidth: 120 }}
                >
                  {dropdownValue ?? 'Select'}
                </Text>
                <IconChevronDown
                  size={16}
                  color="var(--mantine-color-gray-10)"
                  style={{ flexShrink: 0 }}
                />
              </Flex>
            </Menu.Target>
            <Menu.Dropdown>
              {options.length === 0 ? (
                <Menu.Item disabled>No options available</Menu.Item>
              ) : (
                options.map(option => (
                  <Menu.Item key={option.id} onClick={() => onSelectOption(option.id)}>
                    {option.label}
                  </Menu.Item>
                ))
              )}
            </Menu.Dropdown>
          </Menu>
        </SelectorBox>
      </Flex>
    </Box>
  );
}

function SelectorBox({ children }: { children: ReactNode }) {
  return (
    <Flex
      flex={1}
      justify="space-between"
      align="center"
      gap="sm"
      bg="white"
      px="sm"
      py="xs"
      style={{ borderRadius: 16, minWidth: 0 }}
    >
      {children}
    </Flex>
  );
}
